import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import { FieldConfigurationContext } from "../../../lib/context/FieldConfigurationContext";
import { FieldConfigurationProvider } from "../../../lib/context/FieldConfigurationContext";
import TabNavigation from "./TabNavigation";
import { groupRoles } from "./roleUtils";
import { groupUsers } from "./userUtils";
import LicensesDashboard from "./views/LicensesDashboard";
import RolesList from "./views/RolesList";
import RoleForm from "./views/RoleForm";
import RoleDetail from "./views/RoleDetail";
import UsersList from "./views/UsersList";
import UserForm from "./views/UserForm";
import UserDetail from "./views/UserDetail";
import "./style.css";

function FieldConfigurationContent(props) {
  const history = useHistory();
  const location = useLocation();
  const { roles, users, companies, adminPermissions, fetchAdminPermissions } =
    useContext(FieldConfigurationContext);

  // Maps each tab key to the required administration permission
  const TAB_PERMISSIONS = {
    licenses: "view_licenses",
    roles: "configure_roles",
    users: "configure_users",
  };

  // Derive which tabs this user is allowed to see
  const allowedTabs = useMemo(() => {
    const perms = adminPermissions.data;
    if (!perms || !perms.enabled) return [];
    return ["licenses", "roles", "users"].filter(
      (tab) => perms[TAB_PERMISSIONS[tab]]
    );
  }, [adminPermissions.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check authentication and fetch admin permissions on mount
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      history.replace("/login");
      return;
    }
    fetchAdminPermissions();
  }, [history, fetchAdminPermissions]);

  // Once permissions are loaded, verify the user has admin access
  useEffect(() => {
    if (!adminPermissions.loaded || adminPermissions.loading) return;
    if (!adminPermissions.data?.enabled) {
      history.replace("/login");
    }
  }, [
    adminPermissions.loaded,
    adminPermissions.loading,
    adminPermissions.data,
    history,
  ]);

  // Tab state - 'licenses', 'roles', and 'users'
  const [activeTab, setActiveTab] = useState("licenses");

  // State for managing selected items and views
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [capabilityFilter, setCapabilityFilter] = useState(null);

  // Handle default route - redirect to the first tab the user can access
  useEffect(() => {
    if (
      location.pathname === "/admin/field-configuration" &&
      adminPermissions.loaded &&
      allowedTabs.length > 0
    ) {
      history.replace(`/admin/field-configuration/${allowedTabs[0]}`);
    }
  }, [location.pathname, history, adminPermissions.loaded, allowedTabs]);

  // Parse URL and sync to component state
  useEffect(() => {
    // Parse path parts: /admin/field-configuration/[tab]/[id]
    const pathParts = location.pathname.split("/");
    const urlTab = pathParts[3]; // 'licenses', 'roles', 'users', or undefined
    const urlId = pathParts[4]; // UUID or 'new'

    // Parse query params
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter");

    // Determine active tab
    const tab = urlTab || "licenses";

    // Guard: if permissions are loaded and this tab isn't allowed, redirect
    if (
      adminPermissions.loaded &&
      allowedTabs.length > 0 &&
      !allowedTabs.includes(tab)
    ) {
      history.replace(`/admin/field-configuration/${allowedTabs[0]}`);
      return;
    }

    if (tab !== activeTab) {
      setActiveTab(tab);
    }

    // Handle capability filter
    if (filterParam && filterParam !== capabilityFilter) {
      setCapabilityFilter(filterParam);
    } else if (!filterParam && capabilityFilter && tab === "users") {
      // Clear filter if not in URL and we're on users tab
      setCapabilityFilter(null);
    }

    // Handle new form
    if (urlId === "new") {
      if (!showAddForm) {
        setShowAddForm(true);
        setSelectedItem(null);
      }
    }
    // Handle detail view
    else if (urlId && tab !== "licenses") {
      // Find item by UUID
      let item = null;
      if (tab === "roles") {
        const grouped = groupRoles(roles.data, companies.data);
        item = grouped.find(
          (r) => r.uuids?.includes(urlId) || r.uuid === urlId
        );
      } else if (tab === "users") {
        const grouped = groupUsers(users.data, companies.data);
        item = grouped.find(
          (u) => u.uuids?.includes(urlId) || u.uuid === urlId
        );
      }

      if (item) {
        if (!selectedItem || selectedItem.uuid !== item.uuid) {
          setSelectedItem(item);
          setShowAddForm(false);
        }
      } else {
        // Item not found - redirect to list view
        // Only redirect if data is loaded and item still not found
        if (
          (tab === "roles" && roles.data.length > 0) ||
          (tab === "users" && users.data.length > 0)
        ) {
          history.replace(`/admin/field-configuration/${tab}`);
        }
      }
    }
    // Handle list view
    else {
      if (selectedItem || showAddForm) {
        setSelectedItem(null);
        setShowAddForm(false);
      }
    }
  }, [
    location.pathname,
    location.search,
    activeTab,
    selectedItem,
    showAddForm,
    capabilityFilter,
    roles.data,
    users.data,
    companies.data,
    history,
    adminPermissions.loaded,
    allowedTabs,
  ]);

  // Handle tab change and reset state
  const handleTabChange = (tab) => {
    history.push(`/admin/field-configuration/${tab}`);
  };

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedItem(role); // Store full grouped role before URL triggers effect
    history.push(`/admin/field-configuration/roles/${role.uuid}`);
  };

  // Handle add role
  const handleAddRole = () => {
    history.push("/admin/field-configuration/roles/new");
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    history.push(`/admin/field-configuration/users/${user.uuid}`);
  };

  // Handle add user
  const handleAddUser = () => {
    history.push("/admin/field-configuration/users/new");
  };

  // Handle navigation from license view details
  const handleLicenseViewDetails = (licenseType) => {
    // Map license type to capability filter
    const capabilityMap = {
      time: "time",
      expense: "expense",
      project: "projectManagement",
    };

    const filter = capabilityMap[licenseType];
    history.push(`/admin/field-configuration/users?filter=${filter}`);
  };

  // Handle back navigation from detail/form views
  const handleBack = () => {
    history.push(`/admin/field-configuration/${activeTab}`);
  };

  // Handle clearing capability filter
  const handleClearFilter = () => {
    history.push("/admin/field-configuration/users");
  };

  // Show loading state while permissions are being fetched
  if (!adminPermissions.loaded) {
    return (
      <PageLayout {...props}>
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <p>Loading...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout {...props}>
      <div className="field-configuration">
        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          allowedTabs={allowedTabs}
        />

        {/* Tab Content */}
        <div className="field-configuration__content">
          {activeTab === "licenses" && (
            <LicensesDashboard onViewDetails={handleLicenseViewDetails} />
          )}
          {activeTab === "roles" && !selectedItem && !showAddForm && (
            <RolesList
              onRoleSelect={handleRoleSelect}
              onAddRole={handleAddRole}
            />
          )}
          {activeTab === "roles" && showAddForm && (
            <RoleForm onSave={handleBack} onCancel={handleBack} />
          )}
          {activeTab === "roles" && selectedItem && (
            <RoleDetail
              role={selectedItem}
              onBack={handleBack}
              onUpdate={(updatedRole) => {
                if (updatedRole) {
                  setSelectedItem(updatedRole);
                } else {
                  // No grouped role data — navigate to list (roles refreshed in context)
                  handleBack();
                }
              }}
            />
          )}
          {activeTab === "users" && !selectedItem && !showAddForm && (
            <UsersList
              onUserSelect={handleUserSelect}
              onAddUser={handleAddUser}
              capabilityFilter={capabilityFilter}
              onClearFilter={handleClearFilter}
            />
          )}
          {activeTab === "users" && showAddForm && (
            <UserForm onSave={handleBack} onCancel={handleBack} />
          )}
          {activeTab === "users" && selectedItem && (
            <UserDetail
              user={selectedItem}
              onBack={handleBack}
              onUpdate={(updatedUser) => {
                setSelectedItem(updatedUser);
                // List will auto-refresh via context
              }}
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export default function FieldConfiguration(props) {
  return (
    <FieldConfigurationProvider>
      <FieldConfigurationContent {...props} />
    </FieldConfigurationProvider>
  );
}
